import { useSuspenseQuery } from '@tanstack/react-query';

import { getRequest } from '@/api/rest/hooks/requests/get';
import {
  GET_ORGANIZATION_GRAPH_FACTS_PATH,
  GET_ORGANIZATION_REPORTING_FACTS_PATH,
  GetOrganizationGraphFactsErrorDto,
  GetOrganizationGraphFactsRequestQueryParams,
  GetOrganizationGraphFactsSuccessDto,
  GetOrganizationReportingFactsErrorDto,
  GetOrganizationReportingFactsRequestQueryParams,
  GetOrganizationReportingFactsSuccessDto,
} from '@/api/rest/resources/types/organization';
import { usePlotGroupId } from '@/components/PlotGroups/PlotGroupsContextProvider';

import { useNCAParentOrganizationId } from './useNCAParentOrganizationId';

type QueryParams = GetOrganizationReportingFactsRequestQueryParams | GetOrganizationGraphFactsRequestQueryParams;
type SuccessDto = GetOrganizationReportingFactsSuccessDto | GetOrganizationGraphFactsSuccessDto;
type ErrorDto = GetOrganizationReportingFactsErrorDto | GetOrganizationGraphFactsErrorDto;

export const useOrganizationFacts = (
  organizationId: string,
  queryUrl: typeof GET_ORGANIZATION_GRAPH_FACTS_PATH | typeof GET_ORGANIZATION_REPORTING_FACTS_PATH,
  queryParamsArg?: QueryParams,
  plotGroupIdArg?: string | null,
) => {
  const plotGroupId = usePlotGroupId();
  const ncaParentId = useNCAParentOrganizationId();

  const pathVariables = { organization_id: organizationId };
  const queryParams = {
    ...queryParamsArg,
    plot_group_id: plotGroupIdArg ?? plotGroupId,
    ...(ncaParentId ? { nca_parent_id: ncaParentId } : {}),
  };

  const queryFn = async () => {
    return getRequest<SuccessDto>(queryUrl, pathVariables, queryParams);
  };

  return useSuspenseQuery<SuccessDto, ErrorDto>({
    queryKey: ['organization', pathVariables, 'facts', queryParams],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};
